/**
 * Notification title and sentence in the user's language.
 *
 * The backend sends an English message plus codes in the status field
 * ("m=<record model>;r=<reference>;v=<status code>,..."), and the record type
 * in `type` ("prosecution.CaseCreation"). From these the text is written in
 * English, Amharic or Afaan Oromo; without codes (older notifications) the
 * English message is shown.
 */
export type Lang = "en" | "am" | "or";

type Words = Record<Lang, string>;

const RECORDS: Record<string, Words> = {
  ComplaintReception: { en: "discipline complaint", am: "የዲሲፕሊን ቅሬታ", or: "komee naamusaa" },
  Investigation: { en: "discipline investigation", am: "የዲሲፕሊን ምርመራ", or: "qorannoo naamusaa" },
  IssueSummon: { en: "summon", am: "መጥሪያ", or: "waamicha" },
  LitigationVerdictProsecutor: { en: "discipline litigation", am: "የዲሲፕሊን ክርክር", or: "falmii naamusaa" },
  AppealRequest: { en: "discipline appeal", am: "የዲሲፕሊን ይግባኝ", or: "ol'iyyannoo naamusaa" },
  IssueSummonAppeal: { en: "appeal summon", am: "የይግባኝ መጥሪያ", or: "waamicha ol'iyyannoo" },
  IssueSummonAppealByAccusedProsecutor: { en: "appeal by the accused prosecutor", am: "የተከሳሽ ዐቃቤ ሕግ ይግባኝ", or: "ol'iyyannoo abbaa alangaa himatamaa" },
  AppealFromAccusedProsecutorToCourt: { en: "court appeal by the accused prosecutor", am: "የተከሳሽ ዐቃቤ ሕግ የፍርድ ቤት ይግባኝ", or: "ol'iyyannoo mana murtii abbaa alangaa himatamaa" },
  AppealLitigationVerdictProsecutor: { en: "appeal litigation", am: "የይግባኝ ክርክር", or: "falmii ol'iyyannoo" },
  ComplaintReceptionAdvocate: { en: "advocate discipline complaint", am: "የጠበቃ ዲሲፕሊን ቅሬታ", or: "komee naamusa abukaatoo" },
  AdvocateInvestigation: { en: "advocate discipline investigation", am: "የጠበቃ ዲሲፕሊን ምርመራ", or: "qorannoo naamusa abukaatoo" },
  AdvocateIssueSummon: { en: "advocate summon", am: "የጠበቃ መጥሪያ", or: "waamicha abukaatoo" },
  LitigationVerdictAdvocate: { en: "advocate litigation", am: "የጠበቃ ክርክር", or: "falmii abukaatoo" },
  AppealRequestAdvocate: { en: "advocate appeal", am: "የጠበቃ ይግባኝ", or: "ol'iyyannoo abukaatoo" },
  IssueSummonAppealAdvocate: { en: "advocate appeal summon", am: "የጠበቃ ይግባኝ መጥሪያ", or: "waamicha ol'iyyannoo abukaatoo" },
  IssueSummonAppealByAccusedAdvocate: { en: "appeal by the accused advocate", am: "የተከሳሽ ጠበቃ ይግባኝ", or: "ol'iyyannoo abukaatoo himatamaa" },
  AppealFromAccusedAdvocateToCourt: { en: "court appeal by the accused advocate", am: "የተከሳሽ ጠበቃ የፍርድ ቤት ይግባኝ", or: "ol'iyyannoo mana murtii abukaatoo himatamaa" },
  // Page keys (the backend sends the page a notice opens).
  AccusedAppeal: { en: "appeal by the accused prosecutor", am: "የተከሳሽ ዐቃቤ ሕግ ይግባኝ", or: "ol'iyyannoo abbaa alangaa himatamaa" },
  CourtAppeal: { en: "court appeal", am: "የፍርድ ቤት ይግባኝ", or: "ol'iyyannoo mana murtii" },
  AdvocateComplaint: { en: "advocate discipline complaint", am: "የጠበቃ ዲሲፕሊን ቅሬታ", or: "komee naamusa abukaatoo" },
  AdvocateAppeal: { en: "advocate appeal", am: "የጠበቃ ይግባኝ", or: "ol'iyyannoo abukaatoo" },
  AdvocateAccusedAppeal: { en: "appeal by the accused advocate", am: "የተከሳሽ ጠበቃ ይግባኝ", or: "ol'iyyannoo abukaatoo himatamaa" },
  AdvocateCourtAppeal: { en: "advocate court appeal", am: "የጠበቃ የፍርድ ቤት ይግባኝ", or: "ol'iyyannoo mana murtii abukaatoo" },
};

const ACTIONS: Record<string, Words> = {
  assigned: { en: "New assignment", am: "አዲስ ምደባ", or: "Ramaddii haaraa" },
  reassigned: { en: "Reassigned to you", am: "እንደገና ተመድቦልዎታል", or: "Irra deebiin isiniif ramadame" },
  status_changed: { en: "Status update", am: "የሁኔታ ለውጥ", or: "Jijjiirama haalaa" },
  sent_to_you: { en: "Sent to you", am: "ወደ እርስዎ ተልኳል", or: "Gara keessanitti ergame" },
};

const SENTENCES: Record<Lang, Record<string, (record: string, ref: string, status: string) => string>> = {
  en: {
    assigned: (r, ref) => `${/^[aeiou]/i.test(r) ? "An" : "A"} ${r}${ref} has been assigned to you.`,
    reassigned: (r, ref) => `${/^[aeiou]/i.test(r) ? "An" : "A"} ${r}${ref} has been assigned to you.`,
    status_changed: (r, ref, s) => `${cap(r)}${ref} is now: ${s}.`,
    sent_to_you: (r, ref, s) => `${cap(r)}${ref} has been sent to you: ${s}.`,
  },
  am: {
    assigned: (r, ref) => `${r}${ref} ተመድቦልዎታል።`,
    reassigned: (r, ref) => `${r}${ref} ተመድቦልዎታል።`,
    status_changed: (r, ref, s) => `${r}${ref} አሁን፦ ${s}።`,
    sent_to_you: (r, ref, s) => `${r}${ref} ወደ እርስዎ ተልኳል፦ ${s}።`,
  },
  or: {
    assigned: (r, ref) => `${cap(r)}${ref} isiniif ramadameera.`,
    reassigned: (r, ref) => `${cap(r)}${ref} isiniif ramadameera.`,
    status_changed: (r, ref, s) => `${cap(r)}${ref} amma: ${s}.`,
    sent_to_you: (r, ref, s) => `${cap(r)}${ref} gara keessanitti ergameera: ${s}.`,
  },
};

// The statuses these notifications carry, by normalized code.
const STATUS_WORDS: [string[], Words][] = [
  [["forward_to_coordinator", "forwarded_to_coordinator", "forwarded_to_cordinator"],
    { en: "Forwarded to coordinator", am: "ወደ አስተባባሪ ተልኳል", or: "Gara qindeessaatti dabarfame" }],
  [["forwarded_to_higher_coordinator"],
    { en: "Forwarded to higher coordinator", am: "ወደ በላይ አስተባባሪ ተልኳል", or: "Qindeessaa olaanaatti dabarfame" }],
  [["forward_to_registrar", "forwarded_to_registrar"],
    { en: "Forwarded to registrar", am: "ወደ ሬጅስትራር ተልኳል", or: "Gara galmeessaatti dabarfame" }],
  [["forwarded_to_higher_registrar"],
    { en: "Forwarded to higher registrar", am: "ወደ በላይ ሬጅስትራር ተልኳል", or: "Galmeessaa olaanaatti dabarfame" }],
  [["returned_to_coordinator", "respond_back_cordinator"],
    { en: "Returned to coordinator", am: "ወደ አስተባባሪ ተመልሷል", or: "Gara qindeessaatti deebi'e" }],
  [["returned_to_registrar", "responded_back_to_registrar"],
    { en: "Returned to registrar", am: "ወደ ሬጅስትራር ተመልሷል", or: "Gara galmeessaatti deebi'e" }],
  [["respond_back_prosecutor", "returned_to_prosecutor", "responded_back_to_prosecutor",
    "coordinator_responded_back", "respond_back"],
    { en: "Returned to prosecutor", am: "ለዐቃቤ ሕግ ተመልሷል", or: "Abbaa alangaatti deebi'e" }],
  [["sent_to_court", "submitted_to_court", "forwarded_to_court"],
    { en: "Sent to court", am: "ለፍርድ ቤት ተልኳል", or: "Mana murtiitti ergame" }],
  [["assigned_to_prosecutor"],
    { en: "Assigned to prosecutor", am: "ለዐቃቤ ሕግ ተመድቧል", or: "Abbaa alangaaf ramadame" }],
  [["assigned_to_clerk", "forwarded_to_higher_clerk"],
    { en: "Sent to clerk", am: "ወደ ጸሐፊ ተልኳል", or: "Barreessaatti ergame" }],
  [["forwarded_to_director"],
    { en: "Forwarded to director", am: "ወደ ዳይሬክተር ተልኳል", or: "Daarektaraatti dabarfame" }],
  [["forwarded_to_attorney_general", "forwarded_to_head_of_attorney_general"],
    { en: "Forwarded to the attorney general", am: "ወደ ጠቅላይ ዐቃቤ ሕግ ተልኳል", or: "Abbaa alangaa olaanaatti dabarfame" }],
  [["forwarded_to_deputy"],
    { en: "Forwarded to deputy", am: "ወደ ምክትል ተልኳል", or: "Itti aanaatti dabarfame" }],
  [["approved", "granted", "bail_granted", "prohibition_request_approved", "asset_recovery_request_approved"],
    { en: "Approved", am: "ጸድቋል", or: "Raggaasifame" }],
  [["rejected", "reject", "bail_rejected"],
    { en: "Rejected", am: "ውድቅ ተደርጓል", or: "Didame" }],
  [["completed", "recovered"],
    { en: "Completed", am: "ተጠናቋል", or: "Xumurame" }],
  [["final_verdict_given", "prohibition_final_verdict", "asset_recovery_final_verdict"],
    { en: "Final verdict given", am: "የመጨረሻ ፍርድ ተሰጥቷል", or: "Murtiin xumuraa kenname" }],
  [["case_terminated_by_court", "terminated", "case_dismissed", "terminated_by_prosecutor"],
    { en: "Terminated", am: "ተቋርጧል", or: "Addaan citeera" }],
  [["on_appeal"], { en: "On appeal", am: "በይግባኝ ላይ", or: "Ol'iyyannoo irra" }],
  [["on_casation"], { en: "On cassation", am: "በሰበር ላይ", or: "Ijibbaata irra" }],
  [["closed_by_prosecutor", "closed_by_cordinator"],
    { en: "Closed", am: "ተዘግቷል", or: "Cufame" }],
  [["pending"], { en: "Pending", am: "በመጠባበቅ ላይ", or: "Eegaa jira" }],
  [["on_hearing", "on_litigation", "bail_litigation", "prohibition_on_litigation", "asset_recovery_on_litigation"],
    { en: "On hearing", am: "በችሎት ላይ", or: "Dhaddacha irra" }],
  [["case_suspended", "suspended"], { en: "Suspended", am: "ታግዷል", or: "Dhorkame" }],
  [["revised", "on_revision", "prohibition_on_revision", "asset_recovery_on_revision"],
    { en: "Under revision", am: "በክለሳ ላይ", or: "Fooyya'aa jira" }],
  [["forwarded_to_council_writer", "forwareded_to_council_writer", "forwarded_to_council_office", "forwareded_to_council_office", "council_office"],
    { en: "Forwarded to the council office", am: "ወደ ጉባኤ ጽ/ቤት ተልኳል", or: "Waajjira gumiitti dabarfame" }],
  [["forwarded_to_council_head", "forwareded_to_council_head"], { en: "Forwarded to the council head", am: "ወደ ጉባኤ ኃላፊ ተልኳል", or: "Itti gaafatamaa gumiitti dabarfame" }],
  [["forwareded_to_registrar", "forwarded_to_rigistrar"], { en: "Forwarded to registrar", am: "ወደ ሬጅስትራር ተልኳል", or: "Gara galmeessaatti dabarfame" }],
  [["forwareded_to_accused", "forwarded_to_accused"], { en: "Sent to the accused", am: "ለተከሳሹ ተልኳል", or: "Himatamaaf ergame" }],
  [["summon_issued"], { en: "Summon issued", am: "መጥሪያ ወጥቷል", or: "Waamichi baheera" }],
  [["under_investigation"], { en: "Under investigation", am: "በምርመራ ላይ", or: "Qorannaa jira" }],
  [["under_litigation", "on_panel"], { en: "On hearing", am: "በችሎት ላይ", or: "Dhaddacha irra" }],
  [["council_verdict_issued", "decision_issued", "final_decision"], { en: "Decision given", am: "ውሳኔ ተሰጥቷል", or: "Murtiin kenname" }],
  [["revise", "revision", "back_to_prosecutor", "back_to_prosecutor_from_cord", "returned"], { en: "Returned for revision", am: "ለማሻሻያ ተመልሷል", or: "Fooyya'iinsaaf deebi'e" }],
  [["approve"], { en: "Approved", am: "ጸድቋል", or: "Raggaasifame" }],
  [["reject_to_close", "close_the_case", "closed"], { en: "Closed", am: "ተዘግቷል", or: "Cufame" }],
  [["frame_a_charge", "on_charge", "draft_charge"], { en: "Charge to be framed", am: "ክስ ይመሰረታል", or: "Himannaan ni dhiyaata" }],
  [["more_information_requested"], { en: "More information requested", am: "ተጨማሪ መረጃ ተጠይቋል", or: "Odeeffannoon dabalataa gaafatame" }],
  [["appealed", "appealed_by_ins_pro", "appealed_by_accuesd_pro"], { en: "Appealed", am: "ይግባኝ ተብሏል", or: "Ol'iyyatame" }],
  [["accept_the_appeal"], { en: "Appeal accepted", am: "ይግባኙ ተቀባይነት አግኝቷል", or: "Ol'iyyannoon fudhatame" }],
  [["reject_appeal_request", "appeal_rejected_for_inspection", "appeal_rejected_for_accused"], { en: "Appeal rejected", am: "ይግባኙ ውድቅ ተደርጓል", or: "Ol'iyyannoon didame" }],
];

const STATUS_BY_CODE = new Map<string, Words>();
STATUS_WORDS.forEach(([codes, words]) => codes.forEach((c) => STATUS_BY_CODE.set(c, words)));

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const humanize = (code: string) => cap(code.replace(/_/g, " ").toLowerCase());
const normalize = (code: string) => code.trim().toLowerCase().replace(/[\s-]+/g, "_");

export const parseDetails = (status?: string | null) => {
  if (!status || !status.includes("=")) return null;
  const out: { model?: string; reference?: string; values: string[]; regional?: boolean } = { values: [] };
  status.split(";").forEach((part) => {
    const [key, ...rest] = part.split("=");
    const value = rest.join("=");
    if (key === "m") out.model = decodeURIComponent(value);
    if (key === "r") out.reference = decodeURIComponent(value);
    if (key === "v") out.values = value.split(",").filter(Boolean).map(decodeURIComponent);
    if (key === "g") out.regional = value === "1";
  });
  return out;
};

export const statusText = (code: string, lang: Lang, t?: (key: string, opts: any) => unknown) => {
  const words = STATUS_BY_CODE.get(normalize(code));
  if (words) return words[lang];
  return t ? String(t(code, { defaultValue: humanize(code) })) : humanize(code);
};

/** The record model of a notification ("type" is "<app>.<Model>"). */
export const modelOf = (n: any): string =>
  n?.model_name || String(n?.type || "").split(".").pop() || "";

export const notificationText = (
  n: any,
  lang: Lang,
  t?: (key: string, opts: any) => unknown,
): { title: string; message: string } => {
  const details = parseDetails(n?.status);
  const action = String(n?.action || "");
  const recordModel = details?.model || modelOf(n);
  const record = RECORDS[recordModel]?.[lang];
  const actionTitle = ACTIONS[action]?.[lang];
  const title =
    [record && cap(record), actionTitle].filter(Boolean).join(" · ") ||
    (action ? humanize(action) : "");
  const sentence = SENTENCES[lang][action];
  if (!details || !record || !sentence) return { title, message: n?.message || "" };
  const ref = details.reference ? ` '${details.reference}'` : "";
  const status = details.values.map((v) => statusText(v, lang, t)).join(", ");
  return { title, message: sentence(record, ref, status) };
};

const AGO: Record<Lang, { now: string; m: string; h: string; d: string; fmt: (n: number, u: string) => string }> = {
  en: { now: "just now", m: "min", h: "h", d: "d", fmt: (n, u) => `${n} ${u} ago` },
  am: { now: "አሁን", m: "ደቂቃ", h: "ሰዓት", d: "ቀን", fmt: (n, u) => `ከ${n} ${u} በፊት` },
  or: { now: "amma", m: "daqiiqaa", h: "sa'aatii", d: "guyyaa", fmt: (n, u) => `${u} ${n} dura` },
};

/** "5 min ago" / "ከ5 ደቂቃ በፊት" / "daqiiqaa 5 dura". */
export const timeAgoText = (timestamp: string | undefined, lang: Lang) => {
  if (!timestamp) return "";
  const seconds = Math.max(0, (Date.now() - new Date(timestamp).getTime()) / 1000);
  if (Number.isNaN(seconds)) return "";
  const w = AGO[lang];
  if (seconds < 60) return w.now;
  if (seconds < 3600) return w.fmt(Math.floor(seconds / 60), w.m);
  if (seconds < 86400) return w.fmt(Math.floor(seconds / 3600), w.h);
  return w.fmt(Math.floor(seconds / 86400), w.d);
};
